/*
 * QUEST SOFTWARE PROPRIETARY INFORMATION
 *
 * This software is confidential.  Quest Software Inc., or one of its
 * subsidiaries, has supplied this software to you under terms of a
 * license agreement, nondisclosure agreement or both.
 *
 * You may not copy, disclose, or use this software except in accordance with
 * those terms.
 *
 *
 * Copyright 2026 Quest Software Inc.
 * ALL RIGHTS RESERVED.
 *
 * QUEST SOFTWARE INC. MAKES NO REPRESENTATIONS OR
 * WARRANTIES ABOUT THE SUITABILITY OF THE SOFTWARE,
 * EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED
 * TO THE IMPLIED WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE, OR
 * NON-INFRINGEMENT.  QUEST SOFTWARE SHALL NOT BE
 * LIABLE FOR ANY DAMAGES SUFFERED BY LICENSEE
 * AS A RESULT OF USING, MODIFYING OR DISTRIBUTING
 * THIS SOFTWARE OR ITS DERIVATIVES.
 */

import {
  Component,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy,
  Inject,
  ChangeDetectorRef,
  Input,
} from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FacSnackbarOptions, SnackbarService } from '@foglight/angular-common';
import { SidebarHttpService } from '../../services/sidebar-http.service';
import { SidebarService } from '../../services/sidebar.service';
import { NavigationMenuItem } from '../../models/navigation-menu-item';
import { catchError } from 'rxjs/internal/operators/catchError';

@Component({
  selector: 'fc-delete-bookmark-dialog',
  templateUrl: './delete-bookmark-dialog.component.html',
  styleUrls: ['./delete-bookmark-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeleteBookmarkDialogComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  bookmarkName = '';
  deleteBookmarkInfo = 'light-theme-info';
  @Input() inputData: NavigationMenuItem | undefined;

  constructor(
    private dialogRef: MatDialogRef<DeleteBookmarkDialogComponent>,
    private sidebarHttpService: SidebarHttpService,
    private sidebarService: SidebarService,
    private snackbarService: SnackbarService,
    private ref: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.bookmarkName = this.inputData?.displayName || '';
    if(localStorage.getItem('theme') === 'dark-theme') {
      this.deleteBookmarkInfo = 'dark-theme-info';
    }
  }

  deleteBookmark() {
    this.sidebarHttpService
      .deleteBookmark(this.inputData?.id || '')
      .pipe(
        takeUntil(this.destroy$),
        catchError(() => {
          this.dismissDialog('error');
          return [];
        })
      )
      .subscribe(() => {
        this.showSnackbar();
        this.sidebarService.deletedBookmark.next(this.inputData!.id);
        this.ref.detectChanges();
        this.dismissDialog('delete');
      });
  }

  dismissDialog(message: string) {
    this.dialogRef.close(message);
  }

  showSnackbar() {
    const options: FacSnackbarOptions = {
      horizontalPosition: 'end',
      verticalPosition: 'top',
      duration: 3000,
      data: {
        type: 'info',
        text: 'Bookmark was deleted',
      },
    };

    this.snackbarService.show(options);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
